/**
 * useRhymeGrouping Hook — Backend API Edition
 *
 * Previous life: hammered CMU dict locally for phonetic signatures.
 * Current life: ships the whole collection to the backend and lets it figure out
 * the phonetics. Debounced 300ms, race-safe with tokens, no local fallback.
 *
 * When the backend is unavailable, we surface that state and let the UI tell
 * the user. Silently reverting to local logic would be a lie — the two systems
 * don't produce the same categories.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { Word } from '../types/wordCollection';
import type {
  CategorizeCollectionResponse,
  BackendRhymeGroup,
  BackendSingleton,
} from '../types/wordCollection';
import type { RhymeGroup } from '../types/wordCollection';
import { RhymeCategory } from '../utils/rhyme/types';
import { backendApiClient } from '../lib/backendApi';

// ---------------------------------------------------------------------------
// Category string → RhymeCategory enum mapping
// Backend returns lowercase strings; we need the enum for pill coloring.
// ---------------------------------------------------------------------------

const BACKEND_CATEGORY_MAP: Record<string, RhymeCategory> = {
  key_word:         RhymeCategory.PERFECT,   // key word gets group dominant color
  perfect:          RhymeCategory.PERFECT,
  family_voiced:    RhymeCategory.FAMILY_VOICED,
  family_unvoiced:  RhymeCategory.FAMILY_UNVOICED,
  additive:         RhymeCategory.ADDITIVE,
  subtractive:      RhymeCategory.SUBTRACTIVE,
  assonance:        RhymeCategory.ASSONANCE,
  consonance:       RhymeCategory.CONSONANCE,
  none:             RhymeCategory.NONE,
};

function mapBackendCategory(cat: string): RhymeCategory {
  return BACKEND_CATEGORY_MAP[cat.toLowerCase()] ?? RhymeCategory.NONE;
}

// ---------------------------------------------------------------------------
// Convert backend response to the RhymeGroup format used by the sidebar.
// Keeps RhymeGroupCluster's interface untouched — it still gets RhymeGroup[].
// ---------------------------------------------------------------------------

function convertBackendGroups(
  backendGroups: BackendRhymeGroup[],
  backendSingletons: BackendSingleton[]
): RhymeGroup[] {
  const groups: RhymeGroup[] = [];

  for (const bg of backendGroups) {
    // Dominant category: whatever the backend assigned to the key word.
    // Fall back to PERFECT for multi-word groups where key word entry is missing.
    const keyWordEntry = bg.words.find(w => w.id === bg.key_word_id);
    const keyCategory = keyWordEntry
      ? mapBackendCategory(keyWordEntry.category)
      : RhymeCategory.PERFECT;

    groups.push({
      phoneticKey:      bg.phonetic_key,
      words:            bg.words.map(w => w.word),
      dominantCategory: keyCategory,
    });
  }

  // Singletons each get their own group — consistent with the old local behavior
  for (const s of backendSingletons) {
    groups.push({
      phoneticKey:      `__singleton_${s.id}`,
      words:            [s.word],
      dominantCategory: RhymeCategory.NONE,
    });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Per-word category maps — groupPhoneticKey → wordId → RhymeCategory
// Replaces the sidebar's local detectRhymeCategory() effect
// ---------------------------------------------------------------------------

function buildWordCategoryMaps(
  backendGroups: BackendRhymeGroup[]
): Map<string, Map<string, RhymeCategory>> {
  const result = new Map<string, Map<string, RhymeCategory>>();

  for (const bg of backendGroups) {
    const groupMap = new Map<string, RhymeCategory>();
    for (const w of bg.words) {
      groupMap.set(w.id, mapBackendCategory(w.category));
    }
    result.set(bg.phonetic_key, groupMap);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface UseRhymeGroupingResult {
  /** All groups (multi-word and singleton) in RhymeGroup format */
  groups: RhymeGroup[];
  /** Convenience subset: groups where words.length === 1 */
  singletons: RhymeGroup[];
  /** True while an API call is in flight */
  isComputing: boolean;
  /** False when the backend returned an error or is unreachable */
  backendAvailable: boolean;
  /** Word IDs currently in optimistic "computing..." state */
  optimisticWordIds: Set<string>;
  /** Per-word category maps: phoneticKey → wordId → RhymeCategory */
  wordCategoryMaps: Map<string, Map<string, RhymeCategory>>;
  /** Recategorize a group after key-word change */
  recategorizeGroup: (
    groupPhoneticKey: string,
    newKeyWordId: string,
    newKeyWord: string,
    groupWords: Word[]
  ) => Promise<void>;
  /** Incremental categorization for newly added words */
  categorizeNewWords: (keyWord: string, words: string[]) => Promise<void>;
  /** Force a full collection refresh */
  refreshCollection: () => void;
}

const DEBOUNCE_MS = 300;

/**
 * Hook for API-backed rhyme grouping.
 *
 * Accepts the collected word list, key_word_overrides, and an optional songId.
 * Calls categorize-collection on the backend whenever the word set changes.
 * Exposes group results and per-word categories, ready for the sidebar to render.
 *
 * No local phonetics — if the backend is down, backendAvailable goes false
 * and the UI shows a banner. That's the deal.
 */
export function useRhymeGrouping(
  words: Word[],
  keyWordOverrides: Record<string, string> = {},
  songId?: string
): UseRhymeGroupingResult {
  // Version token for race-safe async updates
  const tokenRef = useRef(0);

  // Debounce timer
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable ref so async callbacks can read current words without closing over stale state
  const wordsRef = useRef<Word[]>(words);
  wordsRef.current = words;

  const keyWordOverridesRef = useRef(keyWordOverrides);
  keyWordOverridesRef.current = keyWordOverrides;

  // Refresh counter — bumped by refreshCollection() to force a re-run
  const [refreshTick, setRefreshTick] = useState(0);

  const [groups, setGroups] = useState<RhymeGroup[]>([]);
  const [wordCategoryMaps, setWordCategoryMaps] = useState<Map<string, Map<string, RhymeCategory>>>(new Map());
  const [isComputing, setIsComputing] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [optimisticWordIds, setOptimisticWordIds] = useState<Set<string>>(new Set());

  // Stable word key — prevents infinite loops when caller passes a new array ref on every render
  const wordKey = words
    .map(w => w.id)
    .sort()
    .join('\0');

  const runCategorizeCollection = useCallback(async (token: number) => {
    const currentWords = wordsRef.current;
    const currentOverrides = keyWordOverridesRef.current;

    if (currentWords.length === 0) {
      // Nothing to categorize — reset immediately rather than hitting the network
      if (tokenRef.current === token) {
        setGroups([]);
        setWordCategoryMaps(new Map());
        setIsComputing(false);
      }
      return;
    }

    try {
      const response: CategorizeCollectionResponse = await backendApiClient.categorizeCollection({
        words: currentWords.map(w => ({ id: w.id, word: w.word })),
        key_word_overrides: currentOverrides,
        song_id: songId,
      });

      // Bail if a newer computation has taken over
      if (tokenRef.current !== token) return;

      const converted = convertBackendGroups(response.groups, response.singletons);
      const catMaps = buildWordCategoryMaps(response.groups);

      setGroups(converted);
      setWordCategoryMaps(catMaps);
      setBackendAvailable(true);
      setIsComputing(false);
    } catch {
      // Don't stomp a newer token's completion just because we errored
      if (tokenRef.current !== token) return;

      // Backend down — surface the state, don't silently degrade
      setBackendAvailable(false);
      setIsComputing(false);
    }
  }, [songId]);

  // Re-run when word set changes (or refresh is forced)
  useEffect(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (wordKey === '' && words.length === 0) {
      tokenRef.current++;
      setGroups([]);
      setWordCategoryMaps(new Map());
      setIsComputing(false);
      return;
    }

    setIsComputing(true);

    debounceRef.current = setTimeout(() => {
      const token = ++tokenRef.current;
      runCategorizeCollection(token);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // refreshTick included so refreshCollection() triggers a re-run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordKey, runCategorizeCollection, refreshTick]);

  // ---------------------------------------------------------------------------
  // recategorizeGroup — called when user changes the key word for a group
  // ---------------------------------------------------------------------------

  const recategorizeGroup = useCallback(async (
    groupPhoneticKey: string,
    newKeyWordId: string,
    newKeyWord: string,
    groupWords: Word[]
  ) => {
    try {
      const response = await backendApiClient.recategorizeGroup({
        key_word: { id: newKeyWordId, word: newKeyWord },
        words: groupWords.map(w => ({ id: w.id, word: w.word })),
      });

      // Patch only the target group's categories — leave everything else intact
      setWordCategoryMaps(prev => {
        const next = new Map(prev);
        const groupMap = new Map<string, RhymeCategory>();
        for (const r of response.results) {
          groupMap.set(r.id, mapBackendCategory(r.category));
        }
        next.set(groupPhoneticKey, groupMap);
        return next;
      });

      // Also update dominantCategory on the group itself
      const keyEntry = response.results.find(r => r.id === newKeyWordId);
      const newDominant = keyEntry ? mapBackendCategory(keyEntry.category) : RhymeCategory.PERFECT;

      setGroups(prev =>
        prev.map(g =>
          g.phoneticKey === groupPhoneticKey
            ? { ...g, dominantCategory: newDominant }
            : g
        )
      );

      setBackendAvailable(true);
    } catch {
      setBackendAvailable(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // categorizeNewWords — incremental add for quick-add path
  // ---------------------------------------------------------------------------

  const categorizeNewWords = useCallback(async (keyWord: string, newWords: string[]) => {
    if (newWords.length === 0) return;

    // Find word IDs for the new words so we can show optimistic state
    const newWordIds = wordsRef.current
      .filter(w => newWords.includes(w.word.toLowerCase()))
      .map(w => w.id);

    if (newWordIds.length > 0) {
      setOptimisticWordIds(prev => {
        const next = new Set(prev);
        newWordIds.forEach(id => next.add(id));
        return next;
      });
    }

    try {
      // We don't use this response to update group state incrementally —
      // a full refresh is simpler and keeps the groups consistent.
      // This call's result is used for optimistic UI resolution only.
      await backendApiClient.categorizeWords({ key_word: keyWord, words: newWords });

      // Clear optimistic markers and trigger a full refresh
      setOptimisticWordIds(prev => {
        const next = new Set(prev);
        newWordIds.forEach(id => next.delete(id));
        return next;
      });

      setBackendAvailable(true);
    } catch {
      // Still clear optimistic state — the full refresh will sort it out
      setOptimisticWordIds(prev => {
        const next = new Set(prev);
        newWordIds.forEach(id => next.delete(id));
        return next;
      });
      setBackendAvailable(false);
    }

    // Either way, kick a full re-categorization so groups stay consistent
    setRefreshTick(t => t + 1);
  }, []);

  // ---------------------------------------------------------------------------
  // refreshCollection — force a full re-run without changing the word set
  // ---------------------------------------------------------------------------

  const refreshCollection = useCallback(() => {
    setRefreshTick(t => t + 1);
  }, []);

  const singletons = groups.filter(g => g.words.length === 1);

  return {
    groups,
    singletons,
    isComputing,
    backendAvailable,
    optimisticWordIds,
    wordCategoryMaps,
    recategorizeGroup,
    categorizeNewWords,
    refreshCollection,
  };
}
